import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Snackbar,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useTranslate } from 'react-admin';
import type { BenchmarkResults } from '../utils/MetricsCollector';
import { CloseButton } from './shared/CloseButton';

interface BenchmarkModalProps {
  open: boolean;
  onClose: () => void;
  results: BenchmarkResults | null;
}

export const BenchmarkModal: React.FC<BenchmarkModalProps> = ({
  open,
  onClose,
  results,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopyToClipboard = () => {
    if (!results) return;

    const text = formatResultsAsText(results);

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error('Clipboard write failed:', err);
          fallbackCopy(text);
        });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textarea);
  };

  const formatResultsAsText = (results: BenchmarkResults): string => {
    const lines = [
      translate('osfc.storage_ui.benchmark.text_title'),
      `${translate('osfc.storage_ui.benchmark.timestamp')} ${results.timestamp.toISOString()}`,
      `${translate('osfc.storage_ui.benchmark.total_duration')} ${results.totalDuration.toFixed(0)}ms`,
      '',
      ...results.operations.flatMap((op) => [
        op.operationName,
        `  ${translate('osfc.storage_ui.benchmark.text_duration')} ${op.totalDuration.toFixed(0)}ms`,
        `  ${translate('osfc.storage_ui.benchmark.avg_fps')} ${op.avgFPS.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.one_pct_fps')} ${op.fps1stPercentile.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.text_5pct_fps')} ${op.fps5thPercentile.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.text_median_fps')} ${op.fps50thPercentile.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.min_fps')} ${op.minFPS.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.max_fps')} ${op.maxFPS.toFixed(1)}`,
        `  ${translate('osfc.storage_ui.benchmark.frames')} ${op.frames.length}`,
        '',
      ]),
    ];

    return lines.join('\n');
  };

  if (!results) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{translate('osfc.storage_ui.benchmark.title')}</Typography>
            <CloseButton onClick={onClose}/>
          </div>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{translate('osfc.storage_ui.benchmark.total_duration')}</strong> {results.totalDuration.toFixed(0)}ms
          </Typography>
          <Typography variant="body2" gutterBottom style={{ marginBottom: 20 }}>
            <strong>{translate('osfc.storage_ui.benchmark.timestamp')}</strong> {results.timestamp.toLocaleString()}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('osfc.storage_ui.benchmark.operation')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.duration_ms')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.avg_fps')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.one_pct_fps')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.min_fps')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.max_fps')}</TableCell>
                <TableCell align="right">{translate('osfc.storage_ui.benchmark.frames')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.operations.map((op) => (
                <TableRow key={op.operationId}>
                  <TableCell>{op.operationName}</TableCell>
                  <TableCell align="right">
                    {op.totalDuration.toFixed(0)}
                  </TableCell>
                  <TableCell align="right">{op.avgFPS.toFixed(1)}</TableCell>
                  <TableCell
                    align="right"
                    style={{
                      color:
                        op.fps1stPercentile < 30
                          ? theme.palette.error.main
                          : op.fps1stPercentile < 55
                          ? theme.palette.warning.main
                          : theme.palette.success.main,
                      fontWeight: 'bold',
                    }}
                  >
                    {op.fps1stPercentile.toFixed(1)}
                  </TableCell>
                  <TableCell align="right">{op.minFPS.toFixed(1)}</TableCell>
                  <TableCell align="right">{op.maxFPS.toFixed(1)}</TableCell>
                  <TableCell align="right">{op.frames.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography
            variant="caption"
            style={{ marginTop: 15, display: 'block', color: theme.palette.text.secondary }}
          >
            {translate('osfc.storage_ui.benchmark.note')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCopyToClipboard}
            startIcon={<FileCopyIcon />}
            color="primary"
          >
            {translate('osfc.storage_ui.benchmark.copy')}
          </Button>
          <Button onClick={onClose} color="primary" variant="contained">
            {translate('osfc.storage_ui.benchmark.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={translate('osfc.storage_ui.benchmark.copied')}
      />
    </>
  );
};
